/**
 * WithTherapistView — dedicated PFP tab that mirrors the Clinical
 * parent-facing surface into the unified PFP experience.
 *
 * Data comes from GET /parent-app/with-therapist which reuses existing
 * Clinical tables (weekly_focus, progress notes flagged
 * shared_with_parent, shared_resources, pfp_priority_goals) grouped by
 * therapist_id so duplicate rows show as ONE named therapist.
 *
 * Each section has an independent empty state — an empty goals list
 * doesn't hide feedback, and an empty weekly focus doesn't hide the
 * therapist card.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Stethoscope, Loader2, Target, Sparkles, CalendarClock, MessageSquare,
  BookOpen, MoreHorizontal, Link2Off, X, Share2, Copy,
} from 'lucide-react';
import TherapistLinksCard from './TherapistLinksCard';
import ExternalTherapistCard from './ExternalTherapistCard';
import NotificationsBell from './NotificationsBell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

function EmptyLine({ text }) {
  return <p className="text-sm text-stone-500 py-2">{text}</p>;
}

/**
 * Feb 2026 — PFP-native modal that shows the FULL therapist-shared Weekly
 * Focus (all suggested activities, all modelled language, full reassurance)
 * with share options. Previously this was a `<Link>` to
 * `/weekly-focus/:childId` — a Clinical route that redirects `parent_self`
 * users to `/parent-app/home` (the daily play idea), which is not what the
 * parent asked to see.
 */
function WeeklyFocusDialog({ child, therapist, onClose }) {
  const wf = child?.weekly_focus || {};
  const therapistName = therapist?.therapist_name || therapist?.therapist_email || 'your therapist';
  const childName = child?.parent_child_name || 'your child';

  const shareText = (() => {
    const lines = [];
    lines.push(`Weekly Focus from ${therapistName} for ${childName}`);
    if (wf.focus_statement) lines.push(`\n${wf.focus_statement}`);
    if (Array.isArray(wf.suggested_activities) && wf.suggested_activities.length) {
      lines.push('\nTry at home:');
      wf.suggested_activities.forEach((a) => lines.push(`• ${a}`));
    }
    if (Array.isArray(wf.modeled_language) && wf.modeled_language.length) {
      lines.push('\nLanguage to model:');
      wf.modeled_language.forEach((a) => lines.push(`• "${a}"`));
    }
    if (wf.reassurance_message) lines.push(`\n${wf.reassurance_message}`);
    lines.push('\nShared via PlayFirst');
    return lines.join('\n');
  })();

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success('Copied — paste it anywhere to share');
    } catch {
      toast.error("Couldn't copy — try selecting and copying manually");
    }
  };

  const shareViaWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Weekly Focus', text: shareText });
        return;
      } catch {
        // user cancelled — fall through to copy
      }
    }
    copyToClipboard();
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[10000] bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        data-testid="weekly-focus-dialog-backdrop"
      />
      <div
        role="dialog"
        aria-modal="true"
        className="fixed z-[10001] bg-white shadow-2xl overflow-hidden flex flex-col
                   bottom-0 left-0 right-0 rounded-t-3xl max-h-[92vh]
                   sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:bottom-auto sm:right-auto sm:w-[92vw] sm:max-w-[620px] sm:rounded-3xl sm:max-h-[88vh]"
        data-testid="weekly-focus-dialog"
      >
        <div className="sm:hidden flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 rounded-full bg-stone-300" />
        </div>
        <div className="px-5 py-4 border-b border-stone-100 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-[#7C6F9C]" />
              <span className="text-[11px] uppercase tracking-wider text-[#7C6F9C] font-semibold">
                Weekly Focus
              </span>
            </div>
            <h2 className="font-serif text-xl text-[#3a2f4f] mt-1 leading-snug">
              From {therapistName}
            </h2>
            <p className="text-xs text-stone-500 mt-0.5">for {childName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-stone-100 flex-shrink-0"
            aria-label="Close"
            data-testid="weekly-focus-dialog-close"
          >
            <X className="w-5 h-5 text-stone-500" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4 text-sm text-stone-800">
          {wf.focus_statement && (
            <p className="font-medium text-stone-900" data-testid="weekly-focus-dialog-statement">
              {wf.focus_statement}
            </p>
          )}
          {Array.isArray(wf.suggested_activities) && wf.suggested_activities.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">Try at home</p>
              <ul className="list-disc list-inside space-y-1.5 text-stone-700" data-testid="weekly-focus-dialog-activities">
                {wf.suggested_activities.map((a, i) => (<li key={i}>{a}</li>))}
              </ul>
            </div>
          )}
          {Array.isArray(wf.modeled_language) && wf.modeled_language.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">Language to model</p>
              <ul className="list-disc list-inside space-y-1.5 text-stone-700" data-testid="weekly-focus-dialog-language">
                {wf.modeled_language.map((a, i) => (<li key={i}>"{a}"</li>))}
              </ul>
            </div>
          )}
          {Array.isArray(child?.goals) && child.goals.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">Associated goals</p>
              <ul className="space-y-1.5 text-stone-700">
                {child.goals.slice(0, 8).map((g, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Target className="w-3.5 h-3.5 text-[#7C6F9C] mt-0.5 flex-shrink-0" />
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {wf.reassurance_message && (
            <p className="text-xs italic text-stone-500 border-l-2 border-[#7C6F9C]/30 pl-3">
              {wf.reassurance_message}
            </p>
          )}
          {!wf.focus_statement
            && !(wf.suggested_activities || []).length
            && !(wf.modeled_language || []).length
            && !wf.reassurance_message && (
            <p className="text-sm text-stone-500">No Weekly Focus content yet.</p>
          )}
        </div>

        <div className="px-5 py-3 border-t border-stone-100 flex items-center justify-end gap-2 flex-wrap">
          <button
            onClick={copyToClipboard}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#7C6F9C] hover:bg-[#7C6F9C]/10 rounded-full px-3 py-1.5"
            data-testid="weekly-focus-dialog-copy"
          >
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          <button
            onClick={shareViaWhatsApp}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-[#25D366] hover:bg-[#1EBE5C] rounded-full px-3 py-1.5"
            data-testid="weekly-focus-dialog-whatsapp"
          >
            <Share2 className="w-3.5 h-3.5" /> WhatsApp
          </button>
          <button
            onClick={nativeShare}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-[#7C6F9C] hover:bg-[#6b5f8a] rounded-full px-3 py-1.5"
            data-testid="weekly-focus-dialog-share"
          >
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}

function TherapistSection({ therapist, onDisconnect }) {
  const [openMenu, setOpenMenu] = useState(false);
  // Feb 2026 — Weekly focus opens as an in-place dialog so PFP-side users
  // (who don't have access to the Clinical /weekly-focus route) can still
  // read the full therapist-shared content and share it. Storing the whole
  // { child, weekly_focus } payload rather than an id keeps the modal
  // stateless w.r.t. the Clinical API.
  const [weeklyFocusOpen, setWeeklyFocusOpen] = useState(null);
  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="rounded-3xl bg-white shadow-sm ring-1 ring-stone-200 p-5 md:p-6 space-y-5"
      data-testid={`clinical-therapist-${therapist.therapist_id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[#EAD7D1]/40 flex items-center justify-center flex-shrink-0">
          <Stethoscope className="w-5 h-5 text-[#7C6F9C]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider text-[#7C6F9C] font-medium">
            Connected therapist
          </p>
          <h3 className="font-serif text-xl text-[#3a2f4f]" data-testid={`clinical-therapist-name-${therapist.therapist_id}`}>
            {therapist.therapist_name || therapist.therapist_email}
          </h3>
          <p className="text-xs text-stone-500">
            PlayFirst therapist{therapist.children.length > 0 && ` · connected for ${therapist.children.map((c) => c.parent_child_name || 'your child').join(', ')}`}
          </p>
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpenMenu((o) => !o)}
            className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center"
            aria-label="Manage therapist"
            data-testid={`manage-therapist-${therapist.therapist_id}`}
          >
            <MoreHorizontal className="w-4 h-4 text-stone-500" />
          </button>
          {openMenu && (
            <div className="absolute right-0 mt-1 w-52 bg-white rounded-xl shadow-lg ring-1 ring-stone-200 py-1 z-10">
              <button
                type="button"
                onClick={() => { setOpenMenu(false); onDisconnect(therapist); }}
                className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                data-testid={`disconnect-therapist-${therapist.therapist_id}`}
              >
                <Link2Off className="w-4 h-4" /> Disconnect therapist
              </button>
            </div>
          )}
        </div>
      </div>

      {therapist.children.map((child) => (
        <div key={child.parent_child_id || child.clinical_child_id} className="pl-14 space-y-4">
          {therapist.children.length > 1 && (
            <p className="text-xs font-medium text-stone-700">For {child.parent_child_name || 'your child'}</p>
          )}

          <div>
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-[#7C6F9C]" />
              <h4 className="font-serif text-base text-[#3a2f4f]">Therapist goals</h4>
            </div>
            {child.goals?.length > 0 ? (
              <>
                <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2 mb-2">
                  <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>Your therapist's goals are helping personalise {child.parent_child_name || 'your child'}'s play ideas.</span>
                </div>
                <ul className="space-y-1.5">
                  {child.goals.slice(0, 5).map((g, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-800">
                      <Target className="w-4 h-4 text-[#7C6F9C] mt-0.5 flex-shrink-0" />
                      <span>{g}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : <EmptyLine text="Your therapist hasn't added any goals yet." />}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <CalendarClock className="w-4 h-4 text-[#7C6F9C]" />
              <h4 className="font-serif text-base text-[#3a2f4f]">Weekly focus</h4>
            </div>
            {child.weekly_focus ? (
              <div className="rounded-xl bg-stone-50 p-3 text-sm text-stone-800" data-testid={`weekly-focus-${therapist.therapist_id}`}>
                {child.weekly_focus.focus_statement && (
                  <p className="font-medium mb-2 text-stone-900">{child.weekly_focus.focus_statement}</p>
                )}
                {Array.isArray(child.weekly_focus.suggested_activities) && child.weekly_focus.suggested_activities.length > 0 && (
                  <>
                    <p className="text-xs text-stone-500 mt-2 mb-1">Try at home:</p>
                    <ul className="list-disc list-inside space-y-1 text-stone-700">
                      {child.weekly_focus.suggested_activities.slice(0, 5).map((a, i) => (
                        <li key={i}>{a}</li>
                      ))}
                    </ul>
                  </>
                )}
                {Array.isArray(child.weekly_focus.modeled_language) && child.weekly_focus.modeled_language.length > 0 && (
                  <>
                    <p className="text-xs text-stone-500 mt-3 mb-1">Language to model:</p>
                    <ul className="list-disc list-inside space-y-1 text-stone-700">
                      {child.weekly_focus.modeled_language.slice(0, 5).map((a, i) => (
                        <li key={i}>"{a}"</li>
                      ))}
                    </ul>
                  </>
                )}
                {child.weekly_focus.reassurance_message && (
                  <p className="text-xs italic text-stone-500 mt-3">{child.weekly_focus.reassurance_message}</p>
                )}
                {/* Feb 2026 — Open a PFP-native full Weekly Focus dialog
                    so both PFP-only and dual-role parents can read the
                    complete therapist-shared content without leaving the
                    "With my therapist" tab (previously navigated to a
                    Clinical route that redirects parent_self users to the
                    PFP daily-idea home). */}
                <button
                  type="button"
                  onClick={() => setWeeklyFocusOpen({ child, therapist })}
                  className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-[#7C6F9C] hover:underline"
                  data-testid={`weekly-focus-open-full-${therapist.therapist_id}`}
                >
                  Open full activity & share →
                </button>
              </div>
            ) : <EmptyLine text="No weekly focus has been shared yet." />}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="w-4 h-4 text-[#7C6F9C]" />
              <h4 className="font-serif text-base text-[#3a2f4f]">Session feedback</h4>
            </div>
            {child.feedback?.length > 0 ? (
              <ul className="space-y-2" data-testid={`feedback-list-${therapist.therapist_id}`}>
                {child.feedback.slice(0, 5).map((f) => (
                  <li key={f.id} className="rounded-xl bg-stone-50 p-3">
                    <div className="flex items-center justify-between text-xs text-stone-500 mb-1">
                      <span>{f.date ? new Date(f.date).toLocaleDateString() : ''}</span>
                      <span className="uppercase tracking-wider text-[10px] text-[#7C6F9C]">
                        {f.kind === 'touchpoint' ? 'Touchpoint' : 'Session note'}
                      </span>
                    </div>
                    {f.title && <p className="text-sm font-medium text-stone-900 mb-1">{f.title}</p>}
                    <p className="text-sm text-stone-800 whitespace-pre-wrap">
                      {f.message || 'Update shared.'}
                    </p>
                  </li>
                ))}
              </ul>
            ) : <EmptyLine text="No feedback has been shared yet." />}
          </div>

          {child.shared_resources?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-4 h-4 text-[#7C6F9C]" />
                <h4 className="font-serif text-base text-[#3a2f4f]">Shared resources</h4>
              </div>
              <ul className="space-y-2">
                {child.shared_resources.slice(0, 5).map((s, i) => (
                  <li key={i} className="text-sm text-stone-800">
                    {s.resource?.title || 'Resource'} {s.resource?.external_url && (
                      <a href={s.resource.external_url} target="_blank" rel="noreferrer" className="text-[#7C6F9C] underline">open</a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}

      {weeklyFocusOpen && (
        <WeeklyFocusDialog
          child={weeklyFocusOpen.child}
          therapist={weeklyFocusOpen.therapist}
          onClose={() => setWeeklyFocusOpen(null)}
        />
      )}
    </motion.section>
  );
}

export default function WithTherapistView() {
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);

  const reload = async () => {
    try {
      const [b, c] = await Promise.all([
        axios.get(`${API}/parent-app/with-therapist`, auth()),
        axios.get(`${API}/parent-app/children`, auth()).catch(() => ({ data: [] })),
      ]);
      setTherapists(b.data?.therapists || []);
      setChildren(Array.isArray(c.data) ? c.data : (c.data?.children || []));
    } catch (e) {
      toast.error("Couldn't load therapist connections");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const disconnect = async (therapist) => {
    const label = therapist.therapist_name || therapist.therapist_email;
    if (!window.confirm(
      `Disconnect ${label}?\n\nThey will no longer be an active therapist for your child through PlayFirst. Information already shared may remain in your child's history, but they won't send new updates.`,
    )) return;
    try {
      await Promise.all((therapist.link_ids || []).map((id) =>
        axios.post(`${API}/parent-app/therapist-links/${id}/disconnect`, {}, auth()),
      ));
      toast.success(`${label} disconnected`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not disconnect');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="with-therapist-view">
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        <header className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-white shadow-sm ring-1 ring-stone-200 flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-[#7C6F9C]" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-serif text-2xl md:text-3xl text-stone-900">With my therapist</h1>
            <p className="text-sm text-stone-600 mt-1">
              Everything your therapist has shared with you.
            </p>
          </div>
          {/* Unified in-app notifications — same bell as PFP Home so a
              new weekly focus / touchpoint is surfaced here too. */}
          <NotificationsBell />
        </header>

        <TherapistLinksCard children={children} />

        {therapists.length > 0 ? therapists.map((t) => (
          <TherapistSection key={t.therapist_id} therapist={t} onDisconnect={disconnect} />
        )) : null}

        {/* External / non-PlayFirst therapist connections (iter 94). */}
        <ExternalTherapistCard />

        {therapists.length === 0 && (
          <p className="text-sm text-stone-500 py-6 text-center">
            No PlayFirst Clinical therapist is connected yet.
          </p>
        )}
      </main>
    </div>
  );
}
